import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./dashboard.css";
import "./common-view.css";
import ClientSearch from "./client-search";
import AlertModal from "../components/AlertModal";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import useAuthSession from "../hooks/useAuthSession";
import { BACKEND_BASE_URL } from "../config/appUrls";
import { hasValidPlatformAccess } from "../utils/auth";

const getAccountStatus = (activeEndsAt, t) => {
  if (!activeEndsAt) return { label: t("commonLabel.inactive"), color: "gray" };

  const now = new Date();
  const endDate = new Date(activeEndsAt);
  const yearsInFuture = (endDate - now) / (1000 * 60 * 60 * 24 * 365);

  if (yearsInFuture > 50)
    return {
      label: t("commonLabel.activeUntil", {
        date: String(activeEndsAt).slice(0, 10)
      }),
      color: "blue"
    };
  if (endDate > now) return { label: t("dashboard.onLine"), color: "green" };
  return { label: t("dashboard.offLine"), color: "red" };
};

const CTLG_PAGE_SIZE = 5;

const Dashboard = () => {
  const { t } = useTranslation();
  const auth = useAuthSession();
  const { token, clientId, isAdmin, language } = auth;

  const [selectedClient, setSelectedClient] = useState(null);

  const resolvedClientId = useMemo(() => {
    if (isAdmin) {
      return selectedClient?.client_id || clientId;
    }
    return clientId;
  }, [isAdmin, selectedClient, clientId]);

  const [catalogPage, setCatalogPage] = useState(0);
  const [data, setData] = useState(null);
  const [activeView, setActiveView] = useState("business");
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const [hasTestedBot, setHasTestedBot] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get("tab");
    if (
      tab === "business" ||
      tab === "catalog" ||
      tab === "whatsapp" ||
      tab === "onboarding"
    ) {
      setActiveView(tab);
    }
  }, []);

  const [editMode, setEditMode] = useState(false);
  const [catalogEditMode, setCatalogEditMode] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [productDraft, setProductDraft] = useState(null);
  const [specRows, setSpecRows] = useState([]);
  const [showAiImport, setShowAiImport] = useState(false);
  const [aiImportLoading, setAiImportLoading] = useState(false);
  const [aiImportPreview, setAiImportPreview] = useState(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [stripeReturnMessage, setStripeReturnMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingProductImage, setIsUploadingProductImage] = useState(false);
  const [showCancelSubModal, setShowCancelSubModal] = useState(false);
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null
  });

  const showAlert = (message, title = "", onConfirm = null) => {
    setAlertModal({ open: true, title, message, onConfirm });
  };

  const [adminActionLoading, setAdminActionLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    endpoint: "",
    title: "",
    desc: ""
  });

  const handleClientSelect = useCallback(client => {
    setSelectedClient(client);
  }, []);

  const products = data?.products || [];

  const [confirmSpecDelete, setConfirmSpecDelete] = useState({
    open: false,
    index: null
  });

  const [confirmProductDelete, setConfirmProductDelete] = useState({
    open: false,
    product: null
  });

  const paginatedProducts = products.slice(
    catalogPage * CTLG_PAGE_SIZE,
    (catalogPage + 1) * CTLG_PAGE_SIZE
  );

  const ctlgHasNextPage = (catalogPage + 1) * CTLG_PAGE_SIZE < products.length;
  const ctlgHasPrevPage = catalogPage > 0;

  const addSpecRow = () => {
    setSpecRows(rows => [...rows, { key: "", value: "" }]);
  };

  const updateSpecRow = (idx, field, val) => {
    setSpecRows(rows =>
      rows.map((r, i) => (i === idx ? { ...r, [field]: val } : r))
    );
  };

  const requestRemoveSpecRow = idx => {
    const row = specRows[idx];
    if (!row) return;

    const hasContent =
      (row.key && row.key.trim() !== "") ||
      (row.value && row.value.trim() !== "");

    if (hasContent) {
      setConfirmSpecDelete({ open: true, index: idx });
    } else {
      setSpecRows(rows => rows.filter((_, i) => i !== idx));
    }
  };

  const confirmRemoveSpecRow = () => {
    setSpecRows(rows => rows.filter((_, i) => i !== confirmSpecDelete.index));
    setConfirmSpecDelete({ open: false, index: null });
  };

  const buildSpecificationJson = () => {
    const obj = {};
    specRows.forEach(({ key, value }) => {
      if (key.trim() !== "") {
        obj[key.trim()] = value;
      }
    });
    return Object.keys(obj).length ? obj : null;
  };

  const handleForceOnboarding = async () => {
    if (!data?.client) return;

    setIsCheckoutLoading(true);
    try {
      const capitalize = s =>
        s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

      let tier = capitalize(data.client.tier_plan || "Entrepreneur");
      if (tier === "None") tier = "Entrepreneur";

      let billing = capitalize(data.client.tier_billing || "Monthly");
      if (billing === "None") billing = "Monthly";

      const res = await fetch(
        `${BACKEND_BASE_URL}/api/create-activation-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            planType: tier,
            paymentOption: billing,
            clientId: resolvedClientId
          })
        }
      );

      const json = await res.json();
      if (!res.ok)
        throw new Error(json.error || "Failed to create checkout session");

      if (json.url) {
        window.location.href = json.url;
      }
    } catch (err) {
      console.error(err);
      showAlert(t("dashboard.checkoutFailed", "Failed to start checkout: ") + err.message);
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const executeSubscriptionCancel = async () => {
    try {
      setIsCheckoutLoading(true);
      setShowCancelSubModal(false);

      const res = await fetch(`${BACKEND_BASE_URL}/api/subscription/cancel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: resolvedClientId
        })
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || t("dashboard.cancelFailed", "Cancellation failed"));
      }

      showAlert(json.message, "", () => window.location.reload());
    } catch (err) {
      showAlert(err.message);
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const createEmptyProduct = () => ({
    product_id: null,
    product_type: "Product",
    internal_name: "",
    category: "",
    is_available: true,
    name: "",
    description: "",
    price_retail: 0,
    currency: "USD",
    specification: {},
    stripe_product_id: null,
    stripe_price_id: null
  });

  const handleAddProduct = () => {
    setSelectedProductId(null);
    setCatalogEditMode(true);
    setProductDraft(createEmptyProduct());
    setSpecRows([]);
  };

  const confirmDeleteProduct = async () => {
    const product = confirmProductDelete.product;
    if (!product) return;

    try {
      const targetId = resolvedClientId || data?.client?.client_id;

      const res = await fetch(`${BACKEND_BASE_URL}/api/catalog/master`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          product_id: product.product_id,
          client_id: targetId
        })
      });

      if (!res.ok) {
        throw new Error("Failed deleting product");
      }

      setConfirmProductDelete({ open: false, product: null });
      await reloadCatalog();
    } catch (e) {
      console.error("Delete product failed:", e);
      showAlert(t("dashboard.deleteProductFailed", "Failed to delete product"));
    }
  };

  const hasProductImage =
    Boolean(productDraft?.product_id) &&
    typeof productDraft?.image === "string" &&
    productDraft.image.trim() !== "";

  const saveProduct = async () => {
    const targetId = resolvedClientId || data?.client?.client_id;
    if (!productDraft) return;

    const isNew = !selectedProductId || !productDraft.product_id;
    const method = isNew ? "POST" : "PATCH";

    const r = await fetch(`${BACKEND_BASE_URL}/api/catalog/master`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        client_id: targetId,
        language_code: currentLang,
        product_type: productDraft.product_type,
        internal_name: productDraft.internal_name,
        category: productDraft.category,
        is_available: productDraft.is_available,
        name: productDraft.name,
        description: productDraft.description,
        price_retail:
          productDraft.price_retail === ""
            ? 0
            : Number(productDraft.price_retail),
        currency: productDraft.currency || "USD",
        specification: buildSpecificationJson(),
        product_id: isNew ? undefined : productDraft.product_id
      })
    });

    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      throw new Error(j.error || t("dashboard.failedSavingProduct", "Failed saving catalog item"));
    }

    await reloadCatalog();
    cancelEditProduct();
  };

  const reloadCatalog = async () => {
    try {
      const res = await fetch(
        `${BACKEND_BASE_URL}/api/dashboard?client_id=${resolvedClientId}&language_code=${currentLang}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) return;

      const refreshedData = await res.json();
      setData(refreshedData);
      setCatalogPage(0);
    } catch (err) {
      console.error("reloadCatalog failed:", err);
    }
  };

  const [aiApplyLoading, setAiApplyLoading] = useState(false);
  const [aiImportConflicts, setAiImportConflicts] = useState([]);

  const handleApplyAiImport = async () => {
    if (aiApplyLoading) return;
    setAiApplyLoading(true);
    setAiImportConflicts([]);

    try {
      if (!Array.isArray(aiImportPreview) || aiImportPreview.length === 0) {
        showAlert(t("dashboard.noProductsExtracted"));
        return;
      }

      const targetId = resolvedClientId || data?.client?.client_id;
      const conflicts = [];

      for (const product of aiImportPreview) {
        const res = await fetch(`${BACKEND_BASE_URL}/api/catalog/master`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            client_id: targetId,
            language_code: currentLang,
            product_type: product.product_type || "service",
            internal_name: product.internal_name || product.name,
            name: product.name,
            description: product.description || "",
            category: product.category || "",
            is_available: product.is_available ?? true,
            price_retail: Number(product.price_retail) || 0,
            currency: product.currency || "USD",
            specification: product.specification || null
          })
        });

        if (res.status === 409) {
          conflicts.push(product._displayTitle || product.name);
          continue;
        }

        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || "Failed creating product");
        }
      }

      if (conflicts.length > 0) {
        setAiImportConflicts(conflicts);
        return;
      }

      setShowAiImport(false);
      setAiImportPreview([]);
      await reloadCatalog();
    } catch (e) {
      console.error("Failed to apply AI import:", e);
      showAlert(t("dashboard.failedToSaveImportedProducts"));
    } finally {
      setAiApplyLoading(false);
    }
  };

  const DEFAULT_BUSINESS_HOURS = {
    mon: { closed: false, open: "09:00", close: "17:00" },
    tue: { closed: false, open: "09:00", close: "17:00" },
    wed: { closed: false, open: "09:00", close: "17:00" },
    thu: { closed: false, open: "09:00", close: "17:00" },
    fri: { closed: false, open: "09:00", close: "17:00" },
    sat: { closed: false, open: "09:00", close: "17:00" },
    sun: { closed: true, open: "09:00", close: "17:00" }
  };

  const PAYMENT_METHOD_OPTIONS = [
    { key: "cash", label: t("dashboard.pm.cash") },
    { key: "card", label: t("dashboard.pm.card") },
    { key: "zelle", label: t("dashboard.pm.zelle") },
    { key: "venmo", label: t("dashboard.pm.venmo") },
    { key: "paypal", label: t("dashboard.pm.paypal") },
    { key: "bank_transfer", label: t("dashboard.pm.bankTransfer") }
  ];

  const TIME_OPTIONS = useMemo(() => {
    const out = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const hh = String(h).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        out.push(`${hh}:${mm}`);
      }
    }
    return out;
  }, []);

  const [profileDraft, setProfileDraft] = useState({
    phone: "",
    city: "",
    bot_name: "",
    business_name: "",
    business_type: "",
    business_desc: "",
    location: "",
    payment_methods: [],
    delivery_options: {
      pickup: false,
      delivery: false,
      shipping: false,
      notes: ""
    },
    business_hours: {
      ...DEFAULT_BUSINESS_HOURS,
      ...(data?.botConfig?.business_hours || {})
    },
    persona: "",
    tone: "",
    conversation_guidelines: [],
    example_chat: ""
  });

  const safeObj = v => (v && typeof v === "object" ? v : null);

  const normalizeDeliveryOptions = v => {
    const o = safeObj(v) || {};
    return {
      pickup: !!o.pickup,
      delivery: !!o.delivery,
      shipping: !!o.shipping,
      notes: typeof o.notes === "string" ? o.notes : ""
    };
  };

  const normalizeBusinessHours = v => {
    const defaults = {
      mon: { closed: true, open: "09:00", close: "17:00" },
      tue: { closed: true, open: "09:00", close: "17:00" },
      wed: { closed: true, open: "09:00", close: "17:00" },
      thu: { closed: true, open: "09:00", close: "17:00" },
      fri: { closed: true, open: "09:00", close: "17:00" },
      sat: { closed: true, open: "09:00", close: "17:00" },
      sun: { closed: true, open: "09:00", close: "17:00" }
    };

    const o = safeObj(v) || {};
    const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
    const out = { ...defaults };

    days.forEach(d => {
      const dd = safeObj(o[d]) || {};
      out[d] = {
        closed: dd.closed === undefined ? defaults[d].closed : !!dd.closed,
        open: typeof dd.open === "string" ? dd.open : defaults[d].open,
        close: typeof dd.close === "string" ? dd.close : defaults[d].close
      };
    });

    return out;
  };

  const togglePaymentMethod = key => {
    setProfileDraft(prev => {
      const has = prev.payment_methods.includes(key);
      return {
        ...prev,
        payment_methods: has
          ? prev.payment_methods.filter(x => x !== key)
          : [...prev.payment_methods, key]
      };
    });
  };

  const startEditProduct = p => {
    setSelectedProductId(p.product_id);
    setCatalogEditMode(true);
    setProductDraft({
      product_id: p.product_id,
      image: p.image || null,
      internal_name: p.internal_name || "",
      product_type: p.product_type || "product",
      category: p.category || "",
      is_available: !!p.is_available,
      name: p.name || "",
      description: p.description || "",
      price_retail: p.price_retail ?? "",
      currency: p.currency || "USD",
      specification: p.specification ?? null,
      stripe_product_id: p.stripe_product_id || null,
      stripe_price_id: p.stripe_price_id || null
    });
    const specObj =
      p.specification && typeof p.specification === "object"
        ? p.specification
        : {};

    setSpecRows(
      Object.entries(specObj).map(([key, value]) => ({
        key,
        value: String(value)
      }))
    );
  };

  const cancelEditProduct = () => {
    setCatalogEditMode(false);
    setSelectedProductId(null);
    setProductDraft(null);
    setSpecRows([]);
  };

  const [currentLang, setCurrentLang] = useState(language);

  useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, [currentLang]);

  const currentLocale = useMemo(() => {
    return (currentLang || "en").toLowerCase().startsWith("es")
      ? "es-ES"
      : "en-US";
  }, [currentLang]);

  useEffect(() => {
    if (resolvedClientId === undefined) return;

    const run = async () => {
      try {
        if (!token) {
          window.location.href = "/login";
          return;
        }

        const res = await fetch(
          `${BACKEND_BASE_URL}/api/dashboard?client_id=${resolvedClientId}&language_code=${currentLang}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (res.status === 403) {
          const json = await res.json().catch(() => ({}));
          if (json.code === "PASSWORD_CHANGE_REQUIRED") {
            window.location.href = "/change-password";
            return;
          }
        }

        if (!res.ok) return;

        const data = await res.json();
        setData(data);

        const c = data?.client ?? {};
        const b = data?.botConfig ?? {};
        setProfileDraft({
          phone: c.phone ?? "",
          city: c.city ?? "",
          bot_name: b.bot_name ?? "",
          business_name: b.business_name ?? c.business_name ?? "",
          business_type: b.business_type ?? c.business_type ?? "",
          business_desc: b.business_desc ?? c.business_desc ?? "",
          location: b.location ?? "",
          payment_methods: Array.isArray(b.payment_methods)
            ? b.payment_methods
            : [],
          manual_payment_info: safeObj(b.manual_payment_info) || {},
          delivery_options: normalizeDeliveryOptions(b.delivery_options),
          business_hours: normalizeBusinessHours(b.business_hours),
          persona: b.persona ?? "",
          tone: b.tone ?? "",
          conversation_guidelines: Array.isArray(b.conversation_guidelines)
            ? b.conversation_guidelines
            : [],
          example_chat: b.example_chat
            ? JSON.stringify(b.example_chat, null, 2)
            : ""
        });
      } catch (e) {
        console.error(e);
      }
    };
    run();
  }, [resolvedClientId, currentLang, token]);

  const wabaStatus = useMemo(() => {
    const hasPhoneId = !!data?.client?.waba_phone_number_id;
    if (hasPhoneId) return t("dashboard.connected");
    return t("dashboard.notConnected");
  }, [data, t]);

  const status = getAccountStatus(data?.client?.active_ends_at, t);

  const handleOpenGoLiveModal = () => {
    setShowGoLiveModal(true);
    setHasTestedBot(false);
  };

  const confirmGoLive = async () => {
    try {
      const res = await fetch(`${BACKEND_BASE_URL}/api/client/go-live`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setShowGoLiveModal(false);
        window.location.reload();
      } else {
        const j = await res.json().catch(() => ({}));
        showAlert(j.error || t("dashboard.failedToGoLive", "Failed to go live"));
      }
    } catch (err) {
      console.error("Failed to go live", err);
      showAlert(t("dashboard.errorActivatingBot", "Error activating bot."));
    }
  };

  const handleAdminAction = async endpoint => {
    setAdminActionLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_BASE_URL}/api/admin/clients/${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            client_id: resolvedClientId,
            language: language
          })
        }
      );

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Action failed");

      window.location.reload();
    } catch (err) {
      console.error(err);
    } finally {
      setAdminActionLoading(false);
      setConfirmModal({ show: false, endpoint: "", title: "", desc: "" });
    }
  };

  const handleStripeConnect = async () => {
    try {
      setSyncing(true);

      const createRes = await fetch(
        `${BACKEND_BASE_URL}/api/stripe/create-account`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            client_id: resolvedClientId
          })
        }
      );

      const createJson = await createRes.json();
      if (!createRes.ok)
        throw new Error(createJson.error || "Failed to create Stripe account");

      const onboardRes = await fetch(`${BACKEND_BASE_URL}/api/stripe/onboard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: resolvedClientId
        })
      });

      const onboardJson = await onboardRes.json();
      if (!onboardRes.ok)
        throw new Error(
          onboardJson.error || "Failed to start Stripe onboarding"
        );

      localStorage.setItem("stripe_onboarding_client_id", resolvedClientId);
      window.location.href = onboardJson.url;
    } catch (err) {
      showAlert(t("dashboard.stripeSetupError", "Stripe setup error: ") + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const originalProduct = products.find(
    p => p?.product_id === productDraft?.product_id
  );
  const isUnsavedPriceChange =
    originalProduct &&
    productDraft &&
    Number(productDraft.price_retail) !== Number(originalProduct.price_retail);

  const stripeStatus = data?.client?.stripe_onboarding_status;
  const stripeReady = stripeStatus === "completed";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeReturn = params.get("stripe_return");

    if (stripeReturn !== "success" || !token) return;

    const targetClientId =
      localStorage.getItem("stripe_onboarding_client_id") || resolvedClientId;

    if (!targetClientId) return;

    const checkStripeStatus = async () => {
      try {
        const res = await fetch(
          `${BACKEND_BASE_URL}/api/stripe/account-status`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              client_id: targetClientId
            })
          }
        );

        const json = await res.json();
        if (!res.ok) throw new Error(json.error || "Failed to verify Stripe account");

        if (json.stripe_onboarding_status === "completed") {
          setStripeReturnMessage(t("dashboard.stripeConnectSuccess", "Stripe connected successfully!"));
        } else {
          setStripeReturnMessage(
            `${t("dashboard.stripeStatus", "Stripe status: ")}${json.stripe_onboarding_status}`
          );
        }

        localStorage.removeItem("stripe_onboarding_client_id");

        const dashboardRes = await fetch(
          `${BACKEND_BASE_URL}/api/dashboard?client_id=${targetClientId}&language_code=${currentLang}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (dashboardRes.ok) {
          const refreshedData = await dashboardRes.json();
          setData(refreshedData);
        }

        const cleanUrl = `${window.location.pathname}?tab=onboarding`;
        window.history.replaceState({}, "", cleanUrl);
      } catch (err) {
        setStripeReturnMessage(`${t("dashboard.stripeStatusCheckFailed", "❌ Stripe status check failed: ")}${err.message}`);
      }
    };

    checkStripeStatus();
  }, [token, resolvedClientId, currentLang, t]);

  const handleStripeSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${BACKEND_BASE_URL}/api/catalog/stripe-sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          product_id: productDraft.product_id,
          language_code: currentLang,
          client_id: resolvedClientId
        })
      });

      const resData = await res.json();
      if (!res.ok) throw new Error(resData.error);

      setProductDraft(prev => ({
        ...prev,
        stripe_product_id: resData.stripe_product_id,
        stripe_price_id: resData.stripe_price_id
      }));
      reloadCatalog();
      showAlert(t("dashboard.stripeSyncSuccess"));
    } catch (err) {
      showAlert(t("dashboard.errorSyncing", "Error syncing: ") + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const hasPlatformAccess = useMemo(() => {
    return hasValidPlatformAccess(data?.client);
  }, [data?.client]);

  return (
    <div className="view-page">
      <div className="view-card">
        <div className="view-header">
          <div>
            <h1 className="view-title">
              {data?.botConfig?.business_name || t("dashboard.myBusiness")}
            </h1>

            <p className="view-subtitle">
              {t("commonLabel.plan")}:{" "}
              <strong>{data?.client?.tier_plan || "--"}</strong> ·{" "}
              {t("commonLabel.status")}:{" "}
              <strong style={{ color: status.color }}>{status.label}</strong>
            </p>
          </div>

          <div className="view-nav">
            <select
              className="view-dropdown"
              defaultValue=""
              onChange={e => {
                const route = e.target.value;
                if (!route) return;
                const restrictedRoutes = [
                  "/qr-code",
                  "/support",
                  "/crm-lite",
                  "/scheduler",
                  "/commerce",
                  "/dispatcher",
                  "/analytics"
                ];
                if (!hasPlatformAccess && restrictedRoutes.includes(route)) {
                  window.location.href = "/dashboard?tab=onboarding";
                  return;
                }
                window.location.href = route;
              }}
            >
              <option value="" disabled>{t("dropDown.dashboard-page")}</option>
              <option value="/">{t("dropDown.home-page")}</option>
              <option value="/support">{t("dropDown.support-page")}</option>
              <option value="/qr-code">{t("dropDown.qr-page")}</option>
              <option value="/clients">{t("dropDown.clients-page")}</option>
              <option value="/crm-lite">{t("dropDown.crm-lite-page")}</option>
              <option value="/scheduler">{t("dropDown.scheduler-page")}</option>
              <option value="/commerce">{t("dropDown.commerce-page")}</option>
              <option value="/dispatcher">{t("dropDown.dispatcher-page")}</option>
              <option value="/analytics">{t("dropDown.analytics-page")}</option>
              <option value="/tutorial">{t("dropDown.tutorial-page")}</option>
            </select>
          </div>
        </div>

        <ClientSearch onSelect={handleClientSelect} />

        {data?.client?.onboarding_status === "in_progress" && (
          <div className="go-live-banner">
            <div className="go-live-text">
              <h3>{t("dashboard.almostReady")}</h3>
              <p>{t("dashboard.hubInstructions")}</p>
            </div>
            <button
              className="btn-go-live"
              onClick={handleOpenGoLiveModal}
              disabled={data?.client?.onboarding_step < 6}
              title={
                data?.client?.onboarding_step < 6
                  ? t("dashboard.connectWabaFirst")
                  : t("commonLabel.ready", "Ready")
              }
            >
              🚀 {t("dashboard.goLiveBtn")}
            </button>
          </div>
        )}

        {showGoLiveModal && (
          <div className="launch-modal-overlay">
            <div className="launch-modal-card fade-in">
              <h2>{t("dashboard.launchTitle", "Ready to Launch? 🚀")}</h2>
              <p className="launch-subtitle">
                {t(
                  "dashboard.launchDesc",
                  "Let's review your checklist before releasing your AI assistant to your customers."
                )}
              </p>

              <div className="launch-checklist">
                <div
                  className={`checklist-item ${
                    data?.client?.onboarding_step >= 4 ? "done" : ""
                  }`}
                >
                  <span className="check-icon">
                    {data?.client?.onboarding_step >= 4 ? "✅" : "⏳"}
                  </span>
                  <span className="check-text">
                    {t("dashboard.checkProfile", "Business Profile Complete")}
                  </span>
                </div>

                <div
                  className={`checklist-item ${
                    data?.client?.onboarding_step >= 5 ? "done" : ""
                  }`}
                >
                  <span className="check-icon">
                    {data?.client?.onboarding_step >= 5 ? "✅" : "⏳"}
                  </span>
                  <span className="check-text">
                    {t("dashboard.checkCatalog", "Products & Services Uploaded")}
                  </span>
                </div>

                <div
                  className={`checklist-item ${
                    data?.client?.onboarding_step >= 6 ? "done" : ""
                  }`}
                >
                  <span className="check-icon">
                    {data?.client?.onboarding_step >= 6 ? "✅" : "⏳"}
                  </span>
                  <span className="check-text">
                    {t("dashboard.checkWaba", "WhatsApp Business Linked")}
                  </span>
                </div>

                <div className="checklist-item interactive">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={hasTestedBot}
                      onChange={e => setHasTestedBot(e.target.checked)}
                    />
                    <span className="check-text highlight-text">
                      {t(
                        "dashboard.checkTested",
                        "I have tested my bot via WhatsApp and I am happy with its responses."
                      )}
                    </span>
                  </label>
                </div>
              </div>

              <div className="launch-modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setShowGoLiveModal(false)}
                >
                  {t("commonLabel.cancel", "Cancel")}
                </button>
                <button
                  className="btn-primary"
                  disabled={data?.client?.onboarding_step < 6 || !hasTestedBot}
                  onClick={confirmGoLive}
                >
                  {t("dashboard.activateNow", "Confirm & Activate Now")}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="dashboard-tabs-header">
          <button
            className={`dashboard-tab-btn ${activeView === "business" ? "active" : ""}`}
            disabled={!hasPlatformAccess}
            onClick={() => hasPlatformAccess && setActiveView("business")}
          >
            {t("dashboard.businessInfo")}
          </button>
          <button
            className={`dashboard-tab-btn ${activeView === "catalog" ? "active" : ""}`}
            disabled={!hasPlatformAccess}
            onClick={() => hasPlatformAccess && setActiveView("catalog")}
          >
            {t("dashboard.catalog")}
          </button>
          <button
            className={`dashboard-tab-btn ${activeView === "whatsapp" ? "active" : ""}`}
            disabled={!hasPlatformAccess}
            onClick={() => hasPlatformAccess && setActiveView("whatsapp")}
          >
            {t("dashboard.whatsapp")}
          </button>
          <button
            className={`dashboard-tab-btn ${activeView === "onboarding" ? "active" : ""}`}
            onClick={() => setActiveView("onboarding")}
          >
            {t("dashboard.onboarding")}
          </button>
        </div>

        {activeView === "business" && (
          <div className="view-panel">
            <div className="panel-header">
              <h2>{t("dashboard.businessInfo")}</h2>
              <button
                className="btn-link"
                onClick={() => setEditMode(!editMode)}
              >
                {editMode ? t("commonLabel.cancel") : t("commonLabel.edit")}
              </button>
            </div>

            <div className="form-row">
              <label>{t("commonLabel.phone")}</label>
              {editMode ? (
                <input
                  value={profileDraft.phone}
                  onChange={e =>
                    setProfileDraft({ ...profileDraft, phone: e.target.value })
                  }
                />
              ) : (
                <span>{data?.client?.phone || "—"}</span>
              )}
            </div>

            <div className="form-row">
              <label>{t("dashboard.businessAddress")}</label>
              {editMode ? (
                <input
                  value={profileDraft.city}
                  onChange={e =>
                    setProfileDraft({ ...profileDraft, city: e.target.value })
                  }
                />
              ) : (
                <span>{data?.client?.city || "—"}</span>
              )}
            </div>

            <div className="form-row">
              <label>{t("dashboard.botName")}</label>
              {editMode ? (
                <input
                  value={profileDraft.bot_name}
                  onChange={e =>
                    setProfileDraft({ ...profileDraft, bot_name: e.target.value })
                  }
                />
              ) : (
                <span>{data?.botConfig?.bot_name || "—"}</span>
              )}
            </div>

            <div className="form-row">
              <label>{t("dashboard.location")}</label>
              {editMode ? (
                <input
                  value={profileDraft.location}
                  onChange={e =>
                    setProfileDraft({ ...profileDraft, location: e.target.value })
                  }
                />
              ) : (
                <span>{data?.botConfig?.location || "—"}</span>
              )}
            </div>

            <div className="form-row">
              <label>{t("dashboard.businessName")}</label>
              {editMode ? (
                <input
                  value={profileDraft.business_name}
                  onChange={e =>
                    setProfileDraft({ ...profileDraft, business_name: e.target.value })
                  }
                />
              ) : (
                <span>{data?.botConfig?.business_name || "—"}</span>
              )}
            </div>

            <div className="form-row">
              <label>{t("dashboard.category")}</label>
              {editMode ? (
                <input
                  value={profileDraft.business_type}
                  onChange={e =>
                    setProfileDraft({ ...profileDraft, business_type: e.target.value })
                  }
                />
              ) : (
                <span>{data?.botConfig?.business_type || "—"}</span>
              )}
            </div>

            <div className="form-row">
              <label>{t("dashboard.description")}</label>
              {editMode ? (
                <textarea
                  rows={3}
                  value={profileDraft.business_desc}
                  onChange={e =>
                    setProfileDraft({ ...profileDraft, business_desc: e.target.value })
                  }
                />
              ) : (
                <span>{data?.botConfig?.business_desc || "—"}</span>
              )}
            </div>

            <div className="form-row">
              <label>{t("dashboard.paymentMethods")}</label>
              <div className={`pm-grid ${!editMode ? "readonly" : ""}`}>
                {PAYMENT_METHOD_OPTIONS.map(opt => {
                  const isChecked = editMode
                    ? profileDraft.payment_methods.includes(opt.key)
                    : (data?.botConfig?.payment_methods || []).includes(opt.key);

                  const manualInfo = editMode
                    ? profileDraft.manual_payment_info?.[opt.key] || ""
                    : data?.botConfig?.manual_payment_info?.[opt.key] || "";

                  return (
                    <div key={opt.key} className="pm-item-row">
                      <label className="pm-item">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={!editMode}
                          onChange={editMode ? () => togglePaymentMethod(opt.key) : undefined}
                        />
                        <span>{opt.label}</span>
                      </label>

                      {editMode ? (
                        <input
                          type="text"
                          className="pm-input"
                          placeholder={opt.key === 'card' ? 'Stripe' : t("dashboard.detailsPlaceholder", "Details...")}
                          value={profileDraft.manual_payment_info?.[opt.key] || ""}
                          disabled={!isChecked}
                          onChange={e => {
                            const val = e.target.value;
                            setProfileDraft(prev => ({
                              ...prev,
                              manual_payment_info: {
                                ...prev.manual_payment_info,
                                [opt.key]: val
                              }
                            }));
                          }}
                        />
                      ) : (
                        isChecked && manualInfo ? (
                          <span className="pm-read-info">({manualInfo})</span>
                        ) : null
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="form-row">
              <label>{t("dashboard.deliveryOptions")}</label>
              <div className={`opt-box ${!editMode ? "readonly" : ""}`}>
                {[
                  { key: "pickup", label: t("dashboard.delivery.pickup") },
                  { key: "delivery", label: t("dashboard.delivery.localDelivery") },
                  { key: "shipping", label: t("dashboard.delivery.shipping") }
                ].map(opt => {
                  const source = editMode
                    ? profileDraft.delivery_options
                    : data?.botConfig?.delivery_options || {};

                  const checked = !!source?.[opt.key];

                  return (
                    <label key={opt.key} className="pm-item">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!editMode}
                        onChange={
                          editMode
                            ? () =>
                                setProfileDraft(prev => ({
                                  ...prev,
                                  delivery_options: {
                                    ...prev.delivery_options,
                                    [opt.key]: !prev.delivery_options?.[opt.key]
                                  }
                                }))
                            : undefined
                        }
                      />
                      <span>{opt.label}</span>
                    </label>
                  );
                })}

                <textarea
                  rows={2}
                  placeholder={t("dashboard.delivery.notes")}
                  value={
                    (editMode
                      ? profileDraft.delivery_options?.notes
                      : data?.botConfig?.delivery_options?.notes) || ""
                  }
                  disabled={!editMode}
                  onChange={
                    editMode
                      ? e =>
                          setProfileDraft(prev => ({
                            ...prev,
                            delivery_options: {
                              ...prev.delivery_options,
                              notes: e.target.value
                            }
                          }))
                      : undefined
                  }
                />
              </div>
            </div>

            <div className="form-row">
              <label>{t("dashboard.businessHours")}</label>
              <div className={`hours-table ${!editMode ? "readonly" : ""}`}>
                {[
                  ["mon", t("dashboard.days.mon")],
                  ["tue", t("dashboard.days.tue")],
                  ["wed", t("dashboard.days.wed")],
                  ["thu", t("dashboard.days.thu")],
                  ["fri", t("dashboard.days.fri")],
                  ["sat", t("dashboard.days.sat")],
                  ["sun", t("dashboard.days.sun")]
                ].map(([dayKey, dayLabel]) => {
                  const source = editMode
                    ? profileDraft.business_hours
                    : data?.botConfig?.business_hours;

                  const row = source?.[dayKey] || {
                    closed: true,
                    open: "09:00",
                    close: "17:00"
                  };

                  const isOpen = !row.closed;

                  return (
                    <div key={dayKey} className="hours-row">
                      <div className="hours-day">{dayLabel}</div>

                      <label className="hours-toggle">
                        <input
                          type="checkbox"
                          checked={isOpen}
                          disabled={!editMode}
                          onChange={
                            editMode
                              ? () =>
                                  setProfileDraft(prev => ({
                                    ...prev,
                                    business_hours: {
                                      ...prev.business_hours,
                                      [dayKey]: isOpen
                                        ? { ...row, closed: true }
                                        : {
                                            closed: false,
                                            open: "09:00",
                                            close: "17:00"
                                          }
                                    }
                                  }))
                              : undefined
                          }
                        />
                        <span>
                          {isOpen ? t("commonLabel.open") : t("commonLabel.closed")}
                        </span>
                      </label>

                      <select
                        disabled={!isOpen || !editMode}
                        value={row.open}
                        onChange={
                          editMode
                            ? e =>
                                setProfileDraft(prev => ({
                                  ...prev,
                                  business_hours: {
                                    ...prev.business_hours,
                                    [dayKey]: { ...row, open: e.target.value }
                                  }
                                }))
                            : undefined
                        }
                      >
                        {TIME_OPTIONS.map(tm => (
                          <option key={tm} value={tm}>{tm}</option>
                        ))}
                      </select>

                      <span className="hours-sep">–</span>

                      <select
                        disabled={!isOpen || !editMode}
                        value={row.close}
                        onChange={
                          editMode
                            ? e =>
                                setProfileDraft(prev => ({
                                  ...prev,
                                  business_hours: {
                                    ...prev.business_hours,
                                    [dayKey]: { ...row, close: e.target.value }
                                  }
                                }))
                            : undefined
                        }
                      >
                        {TIME_OPTIONS.map(tm => (
                          <option key={tm} value={tm}>{tm}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="form-row">
              <label>{t("dashboard.persona")}</label>
              {editMode ? (
                <textarea
                  rows={4}
                  value={profileDraft.persona}
                  onChange={e =>
                    setProfileDraft({ ...profileDraft, persona: e.target.value })
                  }
                />
              ) : (
                <span>{data?.botConfig?.persona || "—"}</span>
              )}
            </div>

            <div className="form-row">
              <label>{t("dashboard.tone")}</label>
              {editMode ? (
                <textarea
                  rows={4}
                  value={profileDraft.tone}
                  onChange={e =>
                    setProfileDraft({ ...profileDraft, tone: e.target.value })
                  }
                />
              ) : (
                <span>{data?.botConfig?.tone || "—"}</span>
              )}
            </div>

            <div className="form-row">
              <label>{t("dashboard.conversationGuidelines")}</label>
              {editMode ? (
                <textarea
                  rows={4}
                  value={profileDraft.conversation_guidelines.join("\n")}
                  onChange={e =>
                    setProfileDraft({
                      ...profileDraft,
                      conversation_guidelines: e.target.value
                        .split("\n")
                        .map(x => x.trim())
                        .filter(Boolean)
                    })
                  }
                />
              ) : (
                <span>
                  {(data?.botConfig?.conversation_guidelines || []).join(", ") || "—"}
                </span>
              )}
            </div>

            <div className="form-row">
              <label>{t("dashboard.exampleChat")}</label>
              {editMode ? (
                <textarea
                  rows={4}
                  value={profileDraft.example_chat}
                  onChange={e =>
                    setProfileDraft({ ...profileDraft, example_chat: e.target.value })
                  }
                />
              ) : (
                <pre className="muted small">
                  {JSON.stringify(data?.botConfig?.example_chat, null, 2) || "—"}
                </pre>
              )}
            </div>

            {editMode && (
              <button
                className="btn-primary full"
                disabled={isSaving}
                onClick={async () => {
                  setIsSaving(true);
                  try {
                    const targetId = resolvedClientId || data?.client?.client_id;
                    if (!targetId) {
                      showAlert(t("dashboard.clientIdNotResolved", "Client ID not resolved"));
                      return;
                    }

                    const res = await fetch(`${BACKEND_BASE_URL}/api/client/profile`, {
                      method: "PATCH",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        ...profileDraft,
                        language_code: currentLang,
                        client_id: targetId
                      })
                    });

                    if (!res.ok) {
                      const j = await res.json().catch(() => ({}));
                      showAlert(j.error || t("dashboard.failedToSaveProfile", "Failed to save profile"));
                      return;
                    }

                    setEditMode(false);
                    window.location.reload();
                  } catch (e) {
                    showAlert(e.message);
                  } finally {
                    setIsSaving(false);
                  }
                }}
              >
                {isSaving ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t("dashboard.processing")}
                  </>
                ) : (
                  t("dashboard.saveChanges")
                )}
              </button>
            )}
          </div>
        )}

        {activeView === "catalog" && (
          <div className="view-panel">
            <div className="catalog-header">
              <div className="catalog-header-left">
                <h2>{t("dashboard.catalog")}</h2>
                {!catalogEditMode && (
                  <>
                    <p className="muted small">
                      {t("dashboard.products")}:{" "}
                      <strong>{data?.products?.length ?? 0}</strong>
                    </p>
                    <button
                      className="catalog-add-btn secondary"
                      onClick={() => setShowAiImport(true)}
                    >
                      {t("dashboard.importCatalog")}
                    </button>
                    <p className="catalog-hint small">{t("dashboard.catalogHint")}</p>
                  </>
                )}
              </div>

              {!catalogEditMode && (
                <button className="catalog-add-btn" onClick={handleAddProduct}>
                  {t("dashboard.addProductButton")}
                </button>
              )}

              {catalogEditMode && (
                <button className="btn-link" onClick={cancelEditProduct}>
                  {t("commonLabel.cancel")}
                </button>
              )}
            </div>

            {!catalogEditMode ? (
              <>
                <div className="catalog-list">
                  {paginatedProducts.map(p => (
                    <div key={p.product_id} className="catalog-item-row">
                      <button className="catalog-item" onClick={() => startEditProduct(p)}>
                        {p.image && (
                          <img
                            src={`${BACKEND_BASE_URL}${p.image.replace("main.webp", "thumb.webp")}`}
                            className="catalog-item-image"
                            alt={p.name || p.internal_name}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="catalog-item-title" style={{ wordBreak: "break-word" }}>
                            {p.name || p.internal_name}
                          </div>
                          <div className="catalog-item-sub">
                            {p.price_retail} {p.currency}
                          </div>
                        </div>
                      </button>

                      <button
                        className="catalog-delete-btn side"
                        onClick={e => {
                          e.stopPropagation();
                          setConfirmProductDelete({ open: true, product: p });
                        }}
                        aria-label={t("dashboard.deleteProduct")}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                {products.length > CTLG_PAGE_SIZE && (
                  <div className="common-pagination">
                    {ctlgHasPrevPage && (
                      <button className="btn-link" onClick={() => setCatalogPage(p => p - 1)}>
                        {t("commonLabel.previous")}
                      </button>
                    )}
                    {ctlgHasNextPage && (
                      <button className="btn-link" onClick={() => setCatalogPage(p => p + 1)}>
                        {t("commonLabel.next")}
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="catalog-editor">
                <div className="form-row">
                  <label>{t("dashboard.productType")}</label>
                  <select
                    value={productDraft.product_type}
                    onChange={e =>
                      setProductDraft(prev => ({ ...prev, product_type: e.target.value }))
                    }
                  >
                    <option value="product">{t("dashboard.catalogProduct")}</option>
                    <option value="service">{t("dashboard.catalogService")}</option>
                  </select>
                </div>

                <div className="form-row">
                  <label>{t("dashboard.internalName")}</label>
                  <input
                    value={productDraft.internal_name}
                    onChange={e =>
                      setProductDraft(prev => ({ ...prev, internal_name: e.target.value }))
                    }
                  />
                </div>

                <div className="form-row">
                  <label>{t("dashboard.category")}</label>
                  <input
                    value={productDraft.category}
                    onChange={e =>
                      setProductDraft(prev => ({ ...prev, category: e.target.value }))
                    }
                  />
                </div>

                <div className="form-row">
                  <label>{t("dashboard.available")}</label>
                  <label className="pm-item">
                    <input
                      type="checkbox"
                      checked={!!productDraft.is_available}
                      onChange={() =>
                        setProductDraft(prev => ({ ...prev, is_available: !prev.is_available }))
                      }
                    />
                    <span>
                      {productDraft.is_available ? t("dashboard.yes") : t("dashboard.no")}
                    </span>
                  </label>
                </div>

                <div className="form-row">
                  <label>{t("dashboard.productName")}</label>
                  <input
                    value={productDraft.name ?? ""}
                    onChange={e => setProductDraft(p => ({ ...p, name: e.target.value }))}
                  />
                </div>

                <div className="form-row">
                  <label>{t("dashboard.description")}</label>
                  <textarea
                    rows={3}
                    value={productDraft.description ?? ""}
                    onChange={e => setProductDraft(p => ({ ...p, description: e.target.value }))}
                  />
                </div>

                <div className="form-row">
                  <label>
                    {hasProductImage ? t("dashboard.uploadNewImage") : t("dashboard.productImage")}
                  </label>

                  {productDraft?.product_id ? (
                    <>
                      {hasProductImage && (
                        <img
                          src={`${BACKEND_BASE_URL}${productDraft.image.replace("main.webp", "thumb.webp")}`}
                          className="catalog-editor-image"
                          alt={productDraft.name || productDraft.internal_name}
                        />
                      )}

                      <div className="upload-row">
                        <input
                          id="catalogImageInput"
                          className="file-input-hidden"
                          type="file"
                          accept="image/*"
                          disabled={isUploadingProductImage}
                          onChange={async e => {
                            const fileInput = e.currentTarget;
                            const file = e.target.files?.[0];
                            if (!file) return;

                            const productId = productDraft.product_id;
                            setIsUploadingProductImage(true);

                            try {
                              const targetId = resolvedClientId || data?.client?.client_id;
                              const fd = new FormData();
                              fd.append("image", file);
                              fd.append("client_id", targetId);
                              fd.append("productId", productId);

                              const res = await fetch(`${BACKEND_BASE_URL}/api/catalog/image`, {
                                method: "POST",
                                headers: { Authorization: `Bearer ${token}` },
                                body: fd
                              });
                              const uploaded = await res.json().catch(() => ({}));

                              if (!res.ok || !uploaded.image) {
                                throw new Error(uploaded.error || t("dashboard.failedToUploadImage", "Failed to upload image"));
                              }

                              const cacheBustedImage = `${uploaded.image}?v=${Date.now()}`;
                              setProductDraft(prev =>
                                prev?.product_id === productId ? { ...prev, image: cacheBustedImage } : prev
                              );
                              setData(prev =>
                                prev
                                  ? {
                                      ...prev,
                                      products: (prev.products || []).map(p =>
                                        p.product_id === productId ? { ...p, image: uploaded.image } : p
                                      )
                                    }
                                  : prev
                              );
                            } catch (error) {
                              console.error("Product image upload failed:", error);
                              showAlert(t("dashboard.failedToUploadImage", "Failed to upload image"));
                            } finally {
                              setIsUploadingProductImage(false);
                              fileInput.value = "";
                            }
                          }}
                        />

                        <label
                          htmlFor="catalogImageInput"
                          className="file-btn"
                          aria-disabled={isUploadingProductImage}
                        >
                          {isUploadingProductImage ? t("commonLabel.loading") : t("dashboard.chooseFile")}
                        </label>

                        {!hasProductImage && (
                          <span className="file-hint">{t("dashboard.noFileChosen")}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="muted small">{t("dashboard.saveProductBeforeImage")}</p>
                  )}
                </div>

                <div className="price-currency-row">
                  <div className="form-row">
                    <label>{t("dashboard.retailPrice")}</label>
                    <input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={productDraft.price_retail ?? ""}
                      onChange={e => setProductDraft(p => ({ ...p, price_retail: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-row">
                    <label>{t("dashboard.currency")}</label>
                    <input
                      type="text"
                      value={productDraft.currency ?? "USD"}
                      onChange={e => setProductDraft(p => ({ ...p, currency: e.target.value.toUpperCase() }))}
                      placeholder="USD"
                    />
                  </div>
                </div>

                {productDraft.product_id && (
                  <div
                    className="stripe-sync-section"
                    style={{
                      marginTop: "15px",
                      marginBottom: "15px",
                      padding: "15px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      backgroundColor: "#f9fafb"
                    }}
                  >
                    <h4 style={{ margin: "0 0 10px 0", fontSize: "14px", color: "#374151" }}>
                      💳 {t("dashboard.stripeIntegrationTitle")}
                    </h4>

                    {isUnsavedPriceChange ? (
                      <div style={{ color: "#b45309", fontSize: "13px" }}>
                        <strong>{t("dashboard.stripeUnsavedPriceWarn")}</strong>{" "}
                        {t("dashboard.stripeUnsavedPriceDesc")}
                      </div>
                    ) : productDraft.stripe_price_id ? (
                      <div style={{ color: "#059669", fontSize: "13px" }}>
                        {t("dashboard.stripeSynced")} <br />
                        <span style={{ color: "#6b7280", fontSize: "11px" }}>
                          {t("dashboard.stripePriceId")} {productDraft.stripe_price_id}
                        </span>
                      </div>
                    ) : (
                      <div>
                        <p style={{ fontSize: "13px", color: "#6b7280", margin: "0 0 10px 0" }}>
                          {t("dashboard.stripeNotSyncedDesc")}
                        </p>
                        {!stripeReady ? (
                          <p style={{ fontSize: "13px", color: "#b45309" }}>
                            {t("dashboard.stripeConnectFirst")}
                          </p>
                        ) : (
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={handleStripeSync}
                            disabled={syncing}
                          >
                            {syncing ? t("dashboard.stripeSyncingBtn") : t("dashboard.stripeSyncBtn")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-row">
                  <label>{t("dashboard.specification")}</label>
                </div>

                <div className="form-row">
                  {specRows.map((row, idx) => (
                    <div key={idx} className="spec-row">
                      <input
                        placeholder={t("dashboard.property")}
                        value={row.key}
                        onChange={e => updateSpecRow(idx, "key", e.target.value)}
                      />
                      <input
                        placeholder={t("dashboard.value")}
                        value={row.value}
                        onChange={e => updateSpecRow(idx, "value", e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => requestRemoveSpecRow(idx)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  <button type="button" className="btn-secondary" onClick={addSpecRow}>
                    {t("dashboard.addSpecification")}
                  </button>
                </div>

                <button
                  className="btn-primary full"
                  disabled={isSaving}
                  onClick={async () => {
                    setIsSaving(true);
                    try {
                      await saveProduct();
                    } catch (e) {
                      showAlert(e.message);
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                >
                  {isSaving ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t("dashboard.processing")}
                    </>
                  ) : (
                    t("dashboard.saveChanges")
                  )}
                </button>
              </div>
            )}

            {confirmProductDelete.open && (
              <div className="modal-overlay">
                <div className="modal-card">
                  <h3>{t("dashboard.deleteProductTitle")}</h3>
                  <p>
                    {t("dashboard.confirmDeleteProductMessage", {
                      name: confirmProductDelete.product?.name || confirmProductDelete.product?.internal_name
                    })}
                  </p>
                  <div className="modal-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => setConfirmProductDelete({ open: false, product: null })}
                    >
                      {t("commonLabel.cancel")}
                    </button>
                    <button className="btn-danger" onClick={confirmDeleteProduct}>
                      {t("commonLabel.delete")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {confirmSpecDelete.open && (
              <div className="modal-overlay">
                <div className="modal-card">
                  <h3>{t("dashboard.deleteSpecTitle")}</h3>
                  <p>{t("dashboard.confirmDeleteSpecMessage")}</p>
                  <div className="modal-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => setConfirmSpecDelete({ open: false, index: null })}
                    >
                      {t("commonLabel.cancel")}
                    </button>
                    <button className="btn-danger" onClick={confirmRemoveSpecRow}>
                      {t("commonLabel.delete")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showAiImport && (
              <div className="modal-overlay">
                <div className="modal-card large">
                  <h3>{t("dashboard.importCatalogTitle")}</h3>
                  <p className="muted">{t("dashboard.importCatalogHint")}</p>

                  <label htmlFor="aiCatalogFileInput" className="btn-secondary">
                    {t("dashboard.chooseFile")}
                  </label>

                  <input
                    id="aiCatalogFileInput"
                    type="file"
                    className="file-input-hidden"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.csv,.xlsx,.json,.txt"
                    onChange={async e => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      try {
                        setAiImportLoading(true);
                        setAiImportPreview(null);

                        const fd = new FormData();
                        fd.append("file", file);
                        fd.append("language_code", currentLang);

                        const res = await fetch(`${BACKEND_BASE_URL}/api/catalog/ai-import`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}` },
                          body: fd
                        });

                        const contentType = res.headers.get("content-type") || "";
                        const payload = contentType.includes("application/json")
                          ? await res.json()
                          : { error: await res.text() };

                        if (!res.ok) {
                          showAlert(payload.error || t("dashboard.importCatalogFailed", "Catalog import failed"));
                          setAiImportPreview([]);
                          return;
                        }

                        const productsRaw = Array.isArray(payload.products) ? payload.products : [];
                        const extractedProducts = productsRaw.map(p => {
                          const name = typeof p?.name === "string" ? p.name.trim() : "";
                          const internal_name = typeof p?.internal_name === "string" ? p.internal_name.trim() : "";
                          return {
                            ...p,
                            name,
                            internal_name,
                            _displayTitle: name || internal_name || t("dashboard.untitledProduct"),
                            currency: (p?.currency || "USD").toString().toUpperCase(),
                            price_retail: Number(p?.price_retail) || 0
                          };
                        });

                        setAiImportPreview(extractedProducts);
                        if (extractedProducts.length === 0) {
                          showAlert(t("dashboard.noProductsExtracted"));
                        }
                      } catch (err) {
                        showAlert(err.message || t("dashboard.importCatalogFailed", "Catalog import failed"));
                        setAiImportPreview([]);
                      } finally {
                        setAiImportLoading(false);
                        e.target.value = "";
                      }
                    }}
                  />

                  {aiImportLoading && <p>{t("dashboard.processingFile")}</p>}

                  {Array.isArray(aiImportPreview) && aiImportPreview.length === 0 && !aiImportLoading && (
                    <p className="muted small">{t("dashboard.noProductsExtracted")}</p>
                  )}

                  {Array.isArray(aiImportPreview) && aiImportPreview.length > 0 && (
                    <div className="ai-preview">
                      {aiImportPreview.map((p, idx) => (
                        <div key={idx} className="ai-preview-item">
                          <strong>{p._displayTitle}</strong>
                        </div>
                      ))}
                    </div>
                  )}

                  {aiImportConflicts.length > 0 && (
                    <div className="ai-import-warning">
                      <p className="warning-title">⚠️ {t("dashboard.productsAlreadyExist")}</p>
                      <ul>
                        {aiImportConflicts.map((name, idx) => (
                          <li key={idx}>{name}</li>
                        ))}
                      </ul>
                      <p className="muted small">{t("dashboard.existingProductsSkipped")}</p>
                    </div>
                  )}

                  <div className="modal-actions">
                    <button className="btn-secondary" onClick={() => setShowAiImport(false)}>
                      {t("commonLabel.cancel")}
                    </button>
                    <button
                      className="btn-primary"
                      disabled={aiApplyLoading || !Array.isArray(aiImportPreview) || aiImportPreview.length === 0}
                      onClick={handleApplyAiImport}
                    >
                      {aiApplyLoading ? t("dashboard.processingFile") : t("dashboard.useExtractedData")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeView === "whatsapp" && (
          <div className="view-panel">
            <h2>{t("dashboard.whatsapp")}</h2>
            <p className="muted">
              {t("dashboard.wabaStatus")}: <strong>{wabaStatus}</strong>
            </p>
            <a className="btn-primary full" href="/waba-setup">
              {t("dashboard.connectWhatsApp")}
            </a>
          </div>
        )}

        {activeView === "onboarding" && (
          <div className="view-panel">
            <h2>{t("dashboard.gettingStarted")}</h2>

            {!stripeReady && (
              <div
                className="go-live-banner"
                style={{
                  backgroundColor: "#eef2ff",
                  color: "#3730a3",
                  marginBottom: "20px"
                }}
              >
                <div className="go-live-text">
                  <h3>{t("dashboard.stripeConnectTitle")}</h3>
                  <p>{t("dashboard.stripeConnectDesc")}</p>
                  <p className="muted small">
                    {t("dashboard.stripeCurrentStatus")}{" "}
                    {stripeStatus || "not_started"}
                  </p>
                </div>

                <button
                  className="btn-go-live"
                  onClick={handleStripeConnect}
                  disabled={syncing}
                >
                  💳 {syncing ? t("dashboard.stripeStartingBtn") : t("dashboard.stripeConnectBtn")}
                </button>
              </div>
            )}

            {stripeReturnMessage && (
              <div
                style={{
                  padding: "12px 16px",
                  marginBottom: "16px",
                  borderRadius: "10px",
                  backgroundColor: "#ecfdf5",
                  color: "#065f46",
                  fontWeight: "600"
                }}
              >
                {stripeReturnMessage}
              </div>
            )}

            {stripeReady && (
              <div
                className="go-live-banner"
                style={{
                  backgroundColor: "#ecfdf5",
                  color: "#065f46",
                  marginBottom: "20px"
                }}
              >
                <div className="go-live-text">
                  <h3>{t("dashboard.stripeConnectedTitle", "Stripe Connected")}</h3>
                  <p>
                    {t(
                      "dashboard.stripeConnectedDesc",
                      "Your Stripe Express account is ready. You can now sync products from the Catalog tab."
                    )}
                  </p>
                </div>
              </div>
            )}

            {!(
              data?.client?.subscription_status === "active" ||
              (data?.client?.subscription_status === "canceled" &&
                data?.client?.active_ends_at &&
                new Date(data.client.active_ends_at) > new Date())
            ) && (
              <div
                className="go-live-banner"
                style={{
                  backgroundColor: "#fff3cd",
                  color: "#856404",
                  marginBottom: "20px"
                }}
              >
                <div className="go-live-text">
                  <h3>{t("dashboard.onboardingReq")}</h3>
                  <p>{t("dashboard.onboardingDesc")}</p>
                </div>
                <button
                  className="btn-go-live"
                  style={{ backgroundColor: "#856404" }}
                  onClick={() => handleForceOnboarding()}
                  disabled={isCheckoutLoading}
                >
                  🚀{" "}
                  {isCheckoutLoading
                    ? t("commonLabel.loading")
                    : t("dashboard.onboardingButton")}
                </button>
              </div>
            )}

            {data?.client?.subscription_status === "active" &&
              data?.client?.stripe_subscription_id && (
                <div className="subscription-cancel-box">
                  <span className="subscription-cancel-text">
                    {t("dashboard.cancelSubscriptionTitle")}
                  </span>

                  <button
                    className="subscription-cancel-btn"
                    onClick={() => setShowCancelSubModal(true)}
                    disabled={isCheckoutLoading}
                  >
                    {t("dashboard.cancelSubscriptionButton")}
                  </button>
                </div>
              )}

            {data?.client?.subscription_status === "canceled" &&
              data?.client?.active_ends_at &&
              new Date(data.client.active_ends_at) > new Date() && (
                <div className="subscription-cancel-box">
                  <span className="subscription-cancel-text">
                    {t("dashboard.subscriptionCancellationScheduled", {
                      date: new Date(data.client.active_ends_at).toLocaleDateString(currentLocale)
                    })}
                  </span>{" "}
                </div>
              )}

            {data?.client && isAdmin && (
              <div
                style={{
                  marginTop: "20px",
                  marginBottom: "20px",
                  padding: "16px",
                  background: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px"
                }}
              >
                <h4
                  style={{
                    marginBottom: "12px",
                    color: "#374151",
                    fontSize: "16px",
                    fontWeight: "bold"
                  }}
                >
                  🛠️ {t("dashboard.adminTools.title")}
                </h4>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    className="btn-secondary"
                    style={{
                      background: "#fee2e2",
                      color: "#991b1b",
                      borderColor: "#fecaca"
                    }}
                    onClick={() =>
                      setConfirmModal({
                        show: true,
                        endpoint: "reset-onboarding",
                        title: t("dashboard.adminTools.resetOnboardingTitle"),
                        desc: t("dashboard.adminTools.resetOnboardingDesc")
                      })
                    }
                  >
                    🔄 {t("dashboard.adminTools.resetOnboardingBtn")}
                  </button>

                  <button
                    className="btn-secondary"
                    style={{
                      background: "#e0e7ff",
                      color: "#3730a3",
                      borderColor: "#c7d2fe"
                    }}
                    onClick={() =>
                      setConfirmModal({
                        show: true,
                        endpoint: "reset-password",
                        title: t("dashboard.adminTools.resetPasswordTitle"),
                        desc: t("dashboard.adminTools.resetPasswordDesc")
                      })
                    }
                  >
                    📧 {t("dashboard.adminTools.resetPasswordBtn")}
                  </button>
                </div>
              </div>
            )}

            <div className="onboarding-steps">
              {[
                { key: "start", label: t("dashboard.steps.start", "Start") },
                { key: "waba_connected", label: t("dashboard.steps.connectWaba", "Connect WhatsApp") },
                { key: "profile_done", label: t("dashboard.steps.completeProfile", "Complete Profile") },
                { key: "catalog_done", label: t("dashboard.steps.addProducts", "Add Products") },
                { key: "activate_bot_done", label: t("dashboard.steps.activateBot", "Activate Bot") },
                { key: "active", label: t("dashboard.steps.goLive", "Go Live 🚀") }
              ].map(step => {
                const current = data?.client?.onboarding_status || "start";
                const stepsOrder = [
                  "start",
                  "waba_connected",
                  "profile_done",
                  "catalog_done",
                  "activate_bot_done",
                  "active"
                ];

                const currentIndex = stepsOrder.indexOf(current);
                const stepIndex = stepsOrder.indexOf(step.key);

                const isDone = stepIndex < currentIndex;
                const isCurrent = stepIndex === currentIndex;

                return (
                  <div
                    key={step.key}
                    className={`onboarding-step ${isDone ? "done" : ""} ${isCurrent ? "current" : ""}`}
                  >
                    <div className="step-label">{step.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {showCancelSubModal && (
        <div
          className="confirm-overlay"
          onClick={() => setShowCancelSubModal(false)}
        >
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div
              className="confirm-title"
              style={{ color: "#991b1b", fontWeight: "bold" }}
            >
              ⚠️ {t("dashboard.cancelSubscriptionTitle")}
            </div>

            <div className="confirm-text" style={{ margin: "15px 0" }}>
              {t("dashboard.cancelSubscriptionConfirm")}
            </div>

            <div className="confirm-actions">
              <button
                className="btn-mini"
                onClick={() => setShowCancelSubModal(false)}
                disabled={isCheckoutLoading}
              >
                {t("commonLabel.cancel")}
              </button>

              <button
                className="btn-mini primary"
                style={{ backgroundColor: "#991b1b", borderColor: "#991b1b" }}
                onClick={executeSubscriptionCancel}
                disabled={isCheckoutLoading}
              >
                {isCheckoutLoading
                  ? t("dashboard.processing")
                  : t("commonLabel.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal.show && (
        <div
          className="confirm-overlay"
          onClick={() =>
            setConfirmModal({ show: false, endpoint: "", title: "", desc: "" })
          }
        >
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div
              className="confirm-title"
              style={{ color: "#991b1b", fontWeight: "bold" }}
            >
              {confirmModal.title}
            </div>
            <div className="confirm-text" style={{ margin: "15px 0" }}>
              {confirmModal.desc}
            </div>
            <div className="confirm-actions">
              <button
                className="btn-mini"
                onClick={() =>
                  setConfirmModal({
                    show: false,
                    endpoint: "",
                    title: "",
                    desc: ""
                  })
                }
                disabled={adminActionLoading}
              >
                {t("commonLabel.cancel")}
              </button>
              <button
                className="btn-mini primary"
                onClick={() => handleAdminAction(confirmModal.endpoint)}
                disabled={adminActionLoading}
              >
                {adminActionLoading
                  ? t("dashboard.processing")
                  : t("dashboard.confirmAction")}
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => {
          if (alertModal.onConfirm) alertModal.onConfirm();
          setAlertModal({ open: false, title: "", message: "", onConfirm: null });
        }}
      />
    </div>
  );
};

export default Dashboard;